import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import type { ArticleSummary, Publication, SocialLink } from '@publiqa/shared'
import { formatDate } from '../lib/richtext'
import { useFetch } from '../lib/useFetch'
import { PoweredBy } from '../lib/PoweredBy'
import { useReader } from './reader'
import { SearchIcon, UserIcon } from './ui'
import { isStandalone } from '../admin/pwa'
import { SiteInstall, useSiteApp } from './app'
import { LEGAL_LINKS } from './Legal'
import {
  SmartLink,
  scrollToAnchor,
  useActiveAnchor,
  useLinkState,
  usePublic,
  usePubliqApi,
  usePublicHref,
  useVisibleLinks,
} from './context'

const FONTS: Record<Publication['settings']['theme']['font'], { heading: string; body: string }> = {
  modern: {
    heading: "'Inter Variable', system-ui, sans-serif",
    body: "'Inter Variable', system-ui, sans-serif",
  },
  grotesk: {
    heading: "'Space Grotesk Variable', system-ui, sans-serif",
    body: "'Inter Variable', system-ui, sans-serif",
  },
  editorial: {
    heading: "'Source Serif 4 Variable', Georgia, serif",
    body: "'Inter Variable', system-ui, sans-serif",
  },
}
const RADIUS = { none: '0px', soft: '10px', round: '22px' }
/** Police du nom ; « auto » : celle du gabarit (non définie ici). */
const BRAND_FONTS: Record<string, string> = {
  nunito: "'Nunito Variable', system-ui, sans-serif",
  inter: FONTS.modern.body,
  grotesk: FONTS.grotesk.heading,
  serif: FONTS.editorial.heading,
}

/**
 * Charte eduqor des sites vitrines : police Nunito partout, texte en bleu
 * marine eduqor (--navy #1e3a5f), décliné plus clair pour le texte secondaire
 * et discret.
 */
const EDUQOR = {
  font: "'Nunito Variable', 'Nunito', system-ui, sans-serif",
  ink: '#1e3a5f',
  ink2: '#3d5674',
  ink3: '#6f8196',
}

export function themeStyle(p: Publication): CSSProperties {
  const t = p.settings.theme
  const f = FONTS[t.font] ?? FONTS.modern
  const site = p.kind === 'site'
  return {
    '--primary': t.primary,
    '--accent': t.accent,
    '--font-heading': site ? EDUQOR.font : f.heading,
    '--font-body': site ? EDUQOR.font : f.body,
    '--radius': RADIUS[t.radius] ?? RADIUS.soft,
    ...(site ? { '--ink': EDUQOR.ink, '--ink-2': EDUQOR.ink2, '--ink-3': EDUQOR.ink3 } : {}),
    ...(t.brandFont && BRAND_FONTS[t.brandFont]
      ? { '--font-brand': BRAND_FONTS[t.brandFont] }
      : {}),
  } as CSSProperties
}

const SOCIAL_LABEL: Record<SocialLink['network'], string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  x: 'X',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  tiktok: 'TikTok',
}

function Brand() {
  const { publication } = usePublic()
  const logo = publication.settings.logo
  // Journal : le logo est un emblème discret, toujours accompagné du nom.
  if (publication.kind === 'magazine')
    return (
      <SmartLink href="/" className="brand mg-brand" aria-label={`${publication.name} — accueil`}>
        {logo && <img src={logo.url} alt="" />}
        <span>{publication.name}</span>
      </SmartLink>
    )
  return (
    <SmartLink href="/" className="brand" aria-label={`${publication.name} — accueil`}>
      {logo ? (
        <img src={logo.url} alt={logo.alt || publication.name} />
      ) : (
        <span>{publication.name}</span>
      )}
    </SmartLink>
  )
}

/** Ferme le menu mobile et remonte en haut à chaque navigation (ou va à l'ancre). */
function useNavigationEffects(close: () => void) {
  const location = useLocation()
  const first = useRef(true)
  useEffect(() => {
    close()
    const initial = first.current
    first.current = false
    if (location.hash) scrollToAnchor(location.hash.slice(1), { initial })
    else window.scrollTo(0, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.hash])
}

/** Ancre d'un lien de menu de la page d'accueil (« /#tarifs » → « tarifs »). */
const anchorOf = (href: string) => /^\/?#([\w-]+)$/.exec(href)?.[1] ?? null

function SiteHeader() {
  const { publication, base, inert } = usePublic()
  const s = publication.settings
  const linkState = useLinkState()
  // Bouton d'en-tête retiré s'il mène à un bloc masqué (bulletin fermé…).
  const headerCta = s.headerCta?.label && !linkState(s.headerCta.href).hidden ? s.headerCta : null
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const { pathname } = useLocation()
  useNavigationEffects(() => setOpen(false))
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Surlignage de la rubrique visible — seulement sur l'accueil, où sont les blocs.
  const onHome = pathname.replace(/\/$/, '') === base
  const nav = useVisibleLinks(s.nav)
  const anchors = onHome ? nav.map((l) => anchorOf(l.href)).filter((a): a is string => !!a) : []
  const active = useActiveAnchor(anchors)

  return (
    <header className={`site-header${scrolled ? ' scrolled' : ''}${open ? ' open' : ''}`}>
      <div className="container site-header-inner">
        <Brand />
        <div className="site-header-end">
          <nav id="site-nav" className="site-nav" aria-label="Navigation principale">
            {nav.map((l, i) => {
              const on = !!active && anchorOf(l.href) === active
              return (
                <SmartLink
                  key={i}
                  href={l.href}
                  className={on ? 'active' : undefined}
                  aria-current={on ? 'location' : undefined}
                  onClick={() => setOpen(false)}
                >
                  {l.label}
                </SmartLink>
              )
            })}
            {headerCta && (
              <SmartLink
                href={headerCta.href}
                className="btn btn-primary btn-sm"
                onClick={() => setOpen(false)}
              >
                {headerCta.label}
              </SmartLink>
            )}
          </nav>
          {/* Espace de gestion du club : l'équipe se connecte (présences, inscriptions…). */}
          <a
            className="site-login"
            href={`/admin/${publication.id}`}
            onClick={inert ? (e) => e.preventDefault() : undefined}
            aria-label="Connexion à l’espace de gestion"
            title="Connexion à l’espace de gestion"
          >
            <UserIcon />
          </a>
          <button
            type="button"
            className="burger"
            aria-expanded={open}
            aria-controls="site-nav"
            aria-label="Menu"
            onClick={() => setOpen((o) => !o)}
          >
            <span aria-hidden="true" />
          </button>
        </div>
      </div>
    </header>
  )
}

/**
 * Recherche dans l'en-tête : le champ prend la place des rubriques, les
 * premiers résultats s'affichent dessous ; « Entrée » ouvre la page complète.
 */
function HeaderSearch({
  open,
  onOpen,
  onClose,
}: {
  open: boolean
  onOpen: () => void
  onClose: () => void
}) {
  const path = usePubliqApi()
  const href = usePublicHref()
  const navigate = useNavigate()
  const box = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const [q, setQ] = useState('')
  const [query, setQuery] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 250)
    return () => clearTimeout(t)
  }, [q])
  useEffect(() => {
    if (!open) return
    input.current?.focus()
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])
  const { data, loading } = useFetch<{ articles: ArticleSummary[]; total: number }>(
    open && query.length >= 2 ? path('/articles', { q: query, limit: 6 }) : null,
  )
  const all = () => {
    const to = href('/recherche')
    return `${to}${to.includes('?') ? '&' : '?'}q=${encodeURIComponent(q.trim())}`
  }

  if (!open)
    return (
      <button type="button" className="mg-iconbtn" aria-label="Rechercher" onClick={onOpen}>
        <SearchIcon />
      </button>
    )
  return (
    <div className="mg-hsearch" ref={box}>
      <form
        className="mg-hsearch-form"
        role="search"
        onSubmit={(e) => {
          e.preventDefault()
          if (q.trim().length >= 2) void navigate(all())
        }}
      >
        <SearchIcon />
        <input
          ref={input}
          type="search"
          value={q}
          placeholder="Rechercher un article, un sujet…"
          aria-label="Rechercher dans les articles"
          onChange={(e) => setQ(e.target.value)}
        />
        <button
          type="button"
          className="mg-hsearch-close"
          aria-label="Fermer la recherche"
          onClick={onClose}
        >
          <svg viewBox="0 0 24 24" className="mg-icon" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      </form>
      {query.length >= 2 && (
        <div className="mg-hsearch-pop" role="status">
          {loading && !data ? (
            <p className="mg-hsearch-note">Recherche…</p>
          ) : !data?.articles.length ? (
            <p className="mg-hsearch-note">Aucun article pour « {query} »</p>
          ) : (
            <>
              <ul>
                {data.articles.map((a) => (
                  <li key={a.id}>
                    <SmartLink href={`/article/${a.slug}`}>
                      {a.cover ? (
                        <img src={a.cover.url} alt="" loading="lazy" />
                      ) : (
                        <span className="mg-hsearch-ph" />
                      )}
                      <span>
                        <strong>{a.title}</strong>
                        <small>
                          {[a.category?.name, formatDate(a.publishedAt)]
                            .filter(Boolean)
                            .join(' · ')}
                        </small>
                      </span>
                    </SmartLink>
                  </li>
                ))}
              </ul>
              <Link to={all()} className="mg-hsearch-all">
                {data.total > data.articles.length
                  ? `Voir les ${data.total} résultats`
                  : 'Ouvrir la page de recherche'}{' '}
                →
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function MagazineHeader() {
  const { publication, categories, base } = usePublic()
  const { reader } = useReader()
  const s = publication.settings
  const linkState = useLinkState()
  const { pathname } = useLocation()
  // Bouton d'en-tête retiré s'il mène à un bloc masqué.
  const headerCta = s.headerCta?.label && !linkState(s.headerCta.href).hidden ? s.headerCta : null
  const [open, setOpen] = useState(false)
  const [searching, setSearching] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const closeSearch = useCallback(() => setSearching(false), [])
  useNavigationEffects(() => {
    setOpen(false)
    setSearching(false)
  })
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  const visibleNav = useVisibleLinks(s.nav)
  const top = categories.filter((c) => !c.parentId && c.inNav)
  const all = visibleNav.length
    ? visibleNav
    : top.map((c) => ({ label: c.name, href: `/rubrique/${c.slug}` }))
  // Les rubriques (et « À la une ») sont réunies dans le menu « Rubriques » ;
  // les autres liens (pages, contributeurs…) restent dans la barre.
  const isRubrique = (href: string) => /^\/rubrique\/[\w-]+$/.test(href)
  const rubriques = all.filter((l) => isRubrique(l.href))
  const nav = all.filter((l) => !isRubrique(l.href))
  const current = (href: string) => {
    const slug = /^\/rubrique\/([\w-]+)$/.exec(href)?.[1]
    if (!slug) return false
    const here = /\/rubrique\/([\w-]+)$/.exec(pathname)?.[1]
    const cat = categories.find((c) => c.slug === here)
    const parent = cat?.parentId ? categories.find((c) => c.id === cat.parentId) : undefined
    return here === slug || parent?.slug === slug
  }
  const account = reader ? 'Mon compte' : 'Se connecter'
  // Téléphone : la barre défilante amène la rubrique en cours au centre.
  useEffect(() => {
    const center = () => {
      const bar = document.getElementById('mg-nav')
      const on = bar?.querySelector<HTMLElement>('a[aria-current="page"]')
      if (!bar || !on || bar.scrollWidth <= bar.clientWidth) return
      const shift = on.getBoundingClientRect().left - bar.getBoundingClientRect().left
      bar.scrollLeft += shift - (bar.clientWidth - on.offsetWidth) / 2
    }
    center()
    // Les largeurs changent quand les polices arrivent : on recentre alors.
    void document.fonts?.ready.then(center)
  }, [pathname])

  return (
    <header
      className={`mg-header${s.theme.header === 'primary' ? ' mg-header-primary' : ''}${scrolled ? ' scrolled' : ''}${open ? ' open' : ''}${searching ? ' searching' : ''}`}
    >
      <div className="container mg-header-inner">
        <Brand />
        <div className="mg-navbar">
          <RubriquesMenu
            links={rubriques}
            home={pathname.replace(/\/$/, '') === base}
            current={current}
          />
          <nav id="mg-nav" className="mg-nav" aria-label="Navigation">
            {/* Téléphone : les rubriques à plat (sur ordinateur, dans « Rubriques »). */}
            <SmartLink
              href="/"
              className="mg-nav-rub"
              aria-current={pathname.replace(/\/$/, '') === base ? 'page' : undefined}
            >
              À la une
            </SmartLink>
            {rubriques.map((l, i) => (
              <SmartLink
                key={`r${i}`}
                href={l.href}
                className="mg-nav-rub"
                aria-current={current(l.href) ? 'page' : undefined}
              >
                {l.label}
              </SmartLink>
            ))}
            {/* Téléphone : le fil complet, après les rubriques. */}
            <SmartLink
              href="/actualites"
              className="mg-nav-rub"
              aria-current={
                pathname.replace(/\/$/, '') === `${base}/actualites` ? 'page' : undefined
              }
            >
              Fil
            </SmartLink>
            {/* Ordinateur : les autres liens (sur téléphone, dans le menu). */}
            {nav.map((l, i) => (
              <SmartLink
                key={i}
                href={l.href}
                className="mg-nav-page"
                aria-current={current(l.href) ? 'page' : undefined}
              >
                {l.label}
              </SmartLink>
            ))}
          </nav>
        </div>
        <div className="mg-actions">
          <HeaderSearch
            open={searching}
            onOpen={() => {
              setOpen(false)
              setSearching(true)
            }}
            onClose={closeSearch}
          />
          <SmartLink
            href="/abonnement"
            className="mg-iconbtn mg-account"
            aria-label={account}
            title={account}
          >
            <UserIcon />
          </SmartLink>
          {headerCta && (
            <SmartLink href={headerCta.href} className="mg-cta">
              {headerCta.label}
            </SmartLink>
          )}
          <button
            type="button"
            className="mg-burger"
            aria-expanded={open}
            aria-controls="mg-menu"
            aria-label="Toutes les rubriques"
            onClick={() => {
              setSearching(false)
              setOpen((o) => !o)
            }}
          >
            <span aria-hidden="true" />
          </button>
        </div>
      </div>
      {open && (
        <div id="mg-menu" className="mg-menu">
          {/* Menu en listes groupées (comme les réglages d'un téléphone) :
              une ligne par entrée, séparateurs fins, flèche, page en cours. */}
          <div className="container mg-menu-inner">
            {s.tagline && <p className="mg-menu-tagline">{s.tagline}</p>}
            <div className="mg-menu-groups">
              <section className="mg-menu-group" aria-labelledby="mg-menu-rub">
                <h2 id="mg-menu-rub">Rubriques</h2>
                <ul>
                  <li>
                    <SmartLink
                      href="/"
                      className="mg-row"
                      aria-current={pathname.replace(/\/$/, '') === base ? 'page' : undefined}
                    >
                      <span>À la une</span>
                      <Chevron />
                    </SmartLink>
                  </li>
                  {categories
                    .filter((c) => !c.parentId)
                    .flatMap((c) => [
                      <li key={c.id}>
                        <SmartLink
                          href={`/rubrique/${c.slug}`}
                          className="mg-row"
                          aria-current={current(`/rubrique/${c.slug}`) ? 'page' : undefined}
                        >
                          <span>{c.name}</span>
                          <Chevron />
                        </SmartLink>
                      </li>,
                      ...categories
                        .filter((x) => x.parentId === c.id)
                        .map((x) => (
                          <li key={x.id}>
                            <SmartLink
                              href={`/rubrique/${x.slug}`}
                              className="mg-row mg-row-sub"
                              aria-current={current(`/rubrique/${x.slug}`) ? 'page' : undefined}
                            >
                              <span>{x.name}</span>
                              <Chevron />
                            </SmartLink>
                          </li>
                        )),
                    ])}
                </ul>
              </section>
              {nav.length > 0 && (
                <section className="mg-menu-group" aria-labelledby="mg-menu-pages">
                  <h2 id="mg-menu-pages">{publication.name}</h2>
                  <ul>
                    {nav.map((l, i) => (
                      <li key={i}>
                        <SmartLink
                          href={l.href}
                          className="mg-row"
                          aria-current={
                            pathname.replace(/\/$/, '') === `${base}${l.href}`.replace(/\/$/, '')
                              ? 'page'
                              : undefined
                          }
                        >
                          <span>{l.label}</span>
                          <Chevron />
                        </SmartLink>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              <section className="mg-menu-group" aria-labelledby="mg-menu-me">
                <h2 id="mg-menu-me">Lire et suivre</h2>
                <ul>
                  <li>
                    <SmartLink href="/actualites" className="mg-row">
                      <ListIcon />
                      <span>Le fil des articles</span>
                      <Chevron />
                    </SmartLink>
                  </li>
                  <li>
                    <button
                      type="button"
                      className="mg-row"
                      onClick={() => {
                        setOpen(false)
                        setSearching(true)
                      }}
                    >
                      <SearchIcon />
                      <span>Rechercher</span>
                      <Chevron />
                    </button>
                  </li>
                  <li>
                    <SmartLink href="/abonnement" className="mg-row">
                      <UserIcon />
                      <span>{account}</span>
                      <Chevron />
                    </SmartLink>
                  </li>
                </ul>
                {headerCta && (
                  <SmartLink href={headerCta.href} className="btn btn-primary mg-menu-cta">
                    {headerCta.label}
                  </SmartLink>
                )}
              </section>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}

/** Flèche de fin de ligne (menu). */
function Chevron() {
  return (
    <svg viewBox="0 0 24 24" className="mg-row-chevron" aria-hidden="true">
      <path d="m9 6 6 6-6 6" />
    </svg>
  )
}

/** Icône « fil » (lignes d'articles). */
function ListIcon() {
  return (
    <svg viewBox="0 0 24 24" className="mg-icon" aria-hidden="true">
      <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />
    </svg>
  )
}

/**
 * Menu déroulant « Rubriques » : « À la une » puis les rubriques du menu,
 * chacune avec ses sous-rubriques. Se ferme au clic dehors, avec Échap et à
 * chaque navigation.
 */
function RubriquesMenu({
  links,
  home,
  current,
}: {
  links: { label: string; href: string }[]
  home: boolean
  current: (href: string) => boolean
}) {
  const { categories } = usePublic()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => setOpen(false), [pathname])
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  const here = home || links.some((l) => current(l.href))
  const catOf = (href: string) => categories.find((c) => href === `/rubrique/${c.slug}`)
  return (
    <div className={`mg-rub${open ? ' open' : ''}`} ref={box}>
      <button
        type="button"
        className={`mg-rub-btn${here ? ' on' : ''}`}
        aria-expanded={open}
        aria-controls="mg-rub-panel"
        onClick={() => setOpen((o) => !o)}
      >
        Rubriques
        <svg viewBox="0 0 12 12" width="11" height="11" aria-hidden="true">
          <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      </button>
      {open && (
        <div id="mg-rub-panel" className="mg-rub-panel">
          <SmartLink href="/" className="mg-rub-link" aria-current={home ? 'page' : undefined}>
            À la une
          </SmartLink>
          {links.map((l) => {
            const cat = catOf(l.href)
            const subs = cat ? categories.filter((x) => x.parentId === cat.id) : []
            return (
              <div key={l.href} style={{ '--c': cat?.color } as CSSProperties}>
                <SmartLink
                  href={l.href}
                  className="mg-rub-link mg-rub-cat"
                  aria-current={current(l.href) ? 'page' : undefined}
                >
                  {l.label}
                </SmartLink>
                {subs.map((x) => (
                  <SmartLink
                    key={x.id}
                    href={`/rubrique/${x.slug}`}
                    className="mg-rub-link mg-rub-sub"
                    aria-current={current(`/rubrique/${x.slug}`) ? 'page' : undefined}
                  >
                    {x.name}
                  </SmartLink>
                ))}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function Footer() {
  const { publication } = usePublic()
  const f = publication.settings.footer
  // Liens vers un bloc masqué retirés ; une colonne vidée disparaît.
  const visible = new Set(useVisibleLinks(f.columns.flatMap((c) => c.links)))
  const columns = f.columns
    .map((c) => ({ ...c, links: c.links.filter((l) => visible.has(l)) }))
    .filter((c) => c.links.length)
  return (
    <footer className="site-footer">
      <div className="container footer-grid">
        <div className="footer-about">
          <Brand />
          {f.text && <p>{f.text}</p>}
          {f.social.length > 0 && (
            <ul className="social">
              {f.social.map((s, i) => (
                <li key={i}>
                  <a href={s.href} target="_blank" rel="noopener noreferrer">
                    {SOCIAL_LABEL[s.network]}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
        {columns.map((col, i) => (
          <div key={i} className="footer-col">
            <h2>{col.title}</h2>
            <ul>
              {col.links.map((l, j) => (
                <li key={j}>
                  <SmartLink href={l.href}>{l.label}</SmartLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="container footer-bottom">
        <span>
          © {new Date().getFullYear()} {publication.name}
        </span>
        <nav className="footer-legal" aria-label="Informations légales">
          {LEGAL_LINKS.map((l) => (
            <SmartLink key={l.slug} href={`/${l.slug}`}>
              {l.label}
            </SmartLink>
          ))}
        </nav>
        <PoweredBy className="powered" />
      </div>
    </footer>
  )
}

export function PublicLayout({ children }: { children: ReactNode }) {
  const { publication, preview } = usePublic()
  const isMag = publication.kind === 'magazine'
  useSiteApp()
  return (
    <div
      className={`pub ${isMag ? 'tpl-magazine' : 'tpl-site'}${isStandalone() ? ' is-app' : ''}`}
      style={themeStyle(publication)}
    >
      <a href="#contenu" className="skip">
        Aller au contenu
      </a>
      {preview && <div className="preview-bar">Aperçu — les brouillons sont visibles</div>}
      {isMag ? <MagazineHeader /> : <SiteHeader />}
      <main id="contenu">{children}</main>
      <Footer />
      <SiteInstall />
    </div>
  )
}

/** Titre et description de la page (SEO côté client). */
export function useHead(title: string | undefined, description?: string) {
  const { publication } = usePublic()
  useEffect(() => {
    const s = publication.settings
    const full = title
      ? `${title} — ${publication.name}`
      : s.tagline
        ? `${publication.name} — ${s.tagline}`
        : publication.name
    const desc = description || s.seoDescription || s.tagline
    document.title = full
    // Balises posées par le serveur (référencement) : tenues à jour pendant la navigation.
    const set = (selector: string, attr: string, value: string) =>
      document.querySelector(selector)?.setAttribute(attr, value)
    set('meta[name="description"]', 'content', desc)
    set('meta[property="og:title"]', 'content', full)
    set('meta[property="og:description"]', 'content', desc)
    const here = location.origin + location.pathname
    set('link[rel="canonical"]', 'href', here)
    set('meta[property="og:url"]', 'content', here)
  }, [title, description, publication])
}
